import React, { useState, useEffect } from 'react';
import { feedbacksApi, Feedback } from '../../api/feedbacksApi';
import { toast } from 'react-hot-toast';
import { fileAccessApi } from '../../api/fileAccessApi';
import { ImageModal } from '../ImageModal';
import { useLang } from '../../contexts/LangContext';
import { getFeedbackPriorityLabel, getFeedbackStatusLabel } from '../../utils/feedbackLabels';
import { FeedbackMessagePanel } from '../feedbacks/FeedbackMessagePanel';

interface FeedbackDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  feedbackId: string;
  onRefresh?: () => void;
}

// Helper to check if a file is an image
const isImageFile = (fileName: string) => {
  return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileName);
};

// Helper to get attachment URL
const getAttachmentUrl = async (fileName: string): Promise<string> => {
  if (!fileName) return '';
  if (fileName.startsWith('http')) return fileName;
  
  try {
    // Use fileAccessApi for secure file access
    return await fileAccessApi.getSecureFileUrl(fileName);
  } catch (error) {
    console.error('Error getting secure file URL:', error);
    // Fallback to direct API endpoint for local files
    return `${process.env.REACT_APP_API_URL || 'http://localhost:3000/api'}/feedbacks/attachments/${fileName}`;
  }
};

// Attachment renderer component
const AttachmentRenderer: React.FC<{
  fileName: string;
  index: number;
  onImageClick: (fileName: string) => void;
}> = ({ fileName, index, onImageClick }) => {
  const [url, setUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadUrl = async () => {
      try {
        setLoading(true);
        const secureUrl = await getAttachmentUrl(fileName);
        setUrl(secureUrl);
      } catch (err) {
        console.error('Error loading attachment URL:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadUrl();
  }, [fileName]);

  if (loading) {
    return (
      <div key={index} className="inline-block mr-2">
        <div className="h-24 w-24 bg-gray-200 rounded border border-gray-200 shadow-sm flex items-center justify-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div key={index} className="inline-block mr-2">
        <div className="h-24 w-24 bg-red-100 rounded border border-red-200 shadow-sm flex items-center justify-center">
          <span className="text-red-600 text-xs">Error</span>
        </div>
      </div>
    );
  }

  if (isImageFile(fileName)) {
    return (
      <div key={index} className="inline-block mr-2">
        <img 
          src={url} 
          alt="attachment" 
          className="h-24 rounded border border-gray-200 shadow-sm hover:shadow-md transition cursor-pointer" 
          onClick={() => onImageClick(fileName)}
        />
      </div>
    );
  }
  
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" key={index} className="inline-block mr-2 text-blue-600 underline">
      {fileName.split('/').pop()}
    </a>
  );
};

export const FeedbackDetailsModal: React.FC<FeedbackDetailsModalProps> = ({
  isOpen,
  onClose,
  feedbackId,
  onRefresh
}) => {
  const { t } = useLang();
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  
  // Image modal state
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  useEffect(() => {
    if (isOpen && feedbackId) {
      loadFeedbackDetails();
    }
  }, [isOpen, feedbackId]);

  const loadFeedbackDetails = async () => {
    try {
      setLoading(true);
      const details = await feedbacksApi.getFeedback(feedbackId);
      setFeedback(details);
    } catch (error) {
      console.error('Error loading feedback details:', error);
      toast.error('Lỗi khi tải chi tiết phản ánh');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!feedback) return;
    
    const deadline = prompt('Nhập hạn xử lý (YYYY-MM-DD):');
    if (!deadline) return;
    
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(deadline)) {
      toast.error('Định dạng ngày không hợp lệ. Vui lòng sử dụng YYYY-MM-DD');
      return;
    }
    
    const selectedDate = new Date(deadline);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (selectedDate < today) {
      toast.error('Hạn xử lý không thể là ngày trong quá khứ');
      return;
    }
    
    setConfirming(true);
    try {
      await feedbacksApi.confirmFeedback(feedback.id, { deadline });
      toast.success('Đã xác nhận phản ánh!');
      await loadFeedbackDetails();
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      toast.error('Lỗi khi xác nhận phản ánh');
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    if (onRefresh) {
      onRefresh();
    }
    onClose();
  };

  const openImageModal = (fileName: string) => {
    setSelectedImage(fileName);
    setImageModalOpen(true);
  };

  const closeImageModal = () => {
    setImageModalOpen(false);
    setSelectedImage('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Chi tiết phản ánh</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          {loading || !feedback ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <span className="ml-2 text-gray-600">Đang tải...</span>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <div className="font-mono text-blue-700 font-bold">{feedback.feedbackId}</div>
                <div className="text-gray-700 font-semibold mb-1">{feedback.content}</div>
                {/* Display feedback attachments */}
                {feedback.attachments && feedback.attachments.length > 0 && (
                  <div className="mt-2 mb-2 flex flex-wrap gap-2">
                    {feedback.attachments.map((file, idx) => (
                      <AttachmentRenderer
                        key={idx}
                        fileName={file}
                        index={idx}
                        onImageClick={openImageModal}
                      />
                    ))}
                  </div>
                )}
                <div className="text-sm text-gray-500 mb-1">
                  Trạng thái:{' '}
                  <span className="font-medium">{getFeedbackStatusLabel(feedback.status, t)}</span>
                </div>
                <div className="text-sm text-gray-500 mb-1">Căn hộ: {feedback.apartment.code} - {feedback.apartment.ownerName}</div>
                <div className="text-sm text-gray-500 mb-1">Phân loại: {feedback.category.name}</div>
                <div className="text-sm text-gray-500 mb-1">
                  Mức độ ưu tiên:{' '}
                  <span className="font-medium">{getFeedbackPriorityLabel(feedback.priority, t)}</span>
                </div>
                {feedback.deadline && <div className="text-sm text-gray-500 mb-1">Hạn xử lý: {new Date(feedback.deadline).toLocaleString('vi-VN')}</div>}
              </div>
              {feedback.status === 'PENDING_CONFIRMATION' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 flex items-center justify-between">
                  <span className="text-yellow-800">Bạn cần 'Xác nhận' phản ánh để trao đổi trực tiếp với cư dân</span>
                  <button
                    onClick={handleConfirm}
                    disabled={confirming}
                    className="ml-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {confirming ? 'Đang xác nhận...' : 'Xác nhận'}
                  </button>
                </div>
              )}
              <FeedbackMessagePanel
                feedbackId={feedbackId}
                disabled={feedback.status === 'PENDING_CONFIRMATION'}
                renderAttachment={(fileName, index) => (
                  <AttachmentRenderer
                    key={index}
                    fileName={fileName}
                    index={index}
                    onImageClick={openImageModal}
                  />
                )}
                onMessagesChange={onRefresh}
              />
            </>
          )}
        </div>
      </div>
      <ImageModal
        isOpen={imageModalOpen}
        onClose={closeImageModal}
        fileName={selectedImage}
        fileUrl={selectedImage}
      />
    </div>
  );
}; 